import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, RefreshControl, ActivityIndicator, Modal, Alert, Linking, Platform } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import { AuthManager } from '../utils/authManager';
import {
  customServiceRequestService,
  CustomServiceRequest,
} from '../services/customServiceRequestService';

interface QuoteRequestsScreenProps {
  onBack: () => void;
}

type StatusFilter = 'pending' | 'all';

type ResponseKind = 'interested' | 'not_available' | 'need_more_info';

const QuoteRequestsScreen: React.FC<QuoteRequestsScreenProps> = ({ onBack }) => {
  const [businessId, setBusinessId] = useState<string>('');
  const [businessName, setBusinessName] = useState<string>('');
  const [requests, setRequests] = useState<CustomServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState<StatusFilter>('pending');

  // Response modal state.
  const [activeRequest, setActiveRequest] = useState<CustomServiceRequest | null>(null);
  const [responseKind, setResponseKind] = useState<ResponseKind>('interested');
  const [priceInput, setPriceInput] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadRequests = useCallback(async (ownerId: string) => {
    try {
      const list = await customServiceRequestService.getRequestsForBusiness(ownerId);
      setRequests(list);
    } catch (error) {
      console.error('QuoteRequestsScreen: loadRequests error:', error);
      setRequests([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const owner = await AuthManager.getSavedBusinessOwner();
        if (cancelled) return;
        if (!owner) {
          setLoading(false);
          return;
        }
        setBusinessId(owner.id);
        setBusinessName(owner.companyName || owner.name || 'Your business');
        await loadRequests(owner.id);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadRequests]);

  const onRefresh = useCallback(async () => {
    if (!businessId) return;
    setRefreshing(true);
    try {
      await loadRequests(businessId);
    } finally {
      setRefreshing(false);
    }
  }, [businessId, loadRequests]);

  const visibleRequests = useMemo(() => {
    if (filter === 'pending') return requests.filter(r => r.status === 'pending');
    return requests;
  }, [requests, filter]);

  const pendingCount = useMemo(
    () => requests.filter(r => r.status === 'pending').length,
    [requests]
  );

  const openResponseModal = (request: CustomServiceRequest, kind: ResponseKind) => {
    setActiveRequest(request);
    setResponseKind(kind);
    setPriceInput('');
    setMessageInput('');
  };

  const closeResponseModal = () => {
    if (submitting) return;
    setActiveRequest(null);
    setPriceInput('');
    setMessageInput('');
  };

  const handleSubmitResponse = async () => {
    if (!activeRequest) return;
    if (!businessId) {
      Alert.alert('Not signed in', 'Please sign back in as a business owner to respond.');
      return;
    }

    if (responseKind === 'interested') {
      const parsedPrice = parseFloat(priceInput.replace(/[^0-9.]/g, ''));
      if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
        Alert.alert('Enter a price', 'Please enter a price for your quote.');
        return;
      }
    }

    if (responseKind === 'need_more_info' && !messageInput.trim()) {
      Alert.alert('Add a message', 'Please describe what extra information you need.');
      return;
    }

    setSubmitting(true);
    try {
      const proposedPrice =
        responseKind === 'interested'
          ? parseFloat(priceInput.replace(/[^0-9.]/g, ''))
          : undefined;

      const ok = await customServiceRequestService.respondToRequest(
        activeRequest.id,
        businessId,
        businessName,
        {
          response: responseKind,
          message: messageInput.trim() || undefined,
          proposedPrice,
        }
      );

      if (!ok) {
        Alert.alert('Failed', 'Could not record your response. Please try again.');
        return;
      }

      await loadRequests(businessId);
      setActiveRequest(null);
      setPriceInput('');
      setMessageInput('');

      Alert.alert(
        responseKind === 'interested'
          ? 'Quote sent'
          : responseKind === 'not_available'
          ? 'Marked unavailable'
          : 'Reply saved',
        responseKind === 'interested'
          ? `We've recorded your quote. The customer will see it in their requests.`
          : `Your reply has been saved for the customer.`
      );
    } catch (error) {
      console.error('QuoteRequestsScreen: respond error:', error);
      Alert.alert('Error', 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderRequestCard = (req: CustomServiceRequest) => {
    const isPending = req.status === 'pending';
    const myResponses = (req.responses || []).filter(r => r.businessId === businessId);
    const created = formatRelative(req.createdAt);

    return (
      <View key={req.id} style={styles.card}>
        <View style={styles.cardHeaderRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.cardTitle}>{req.serviceName || 'Service request'}</Text>
            <Text style={styles.cardSubtitle}>
              From {req.userName || 'Customer'}
              {created ? ` · ${created}` : ''}
            </Text>
          </View>
          <StatusBadge status={req.status} />
        </View>

        {req.requestSubtype === 'transportation_quote' && (
          <View style={styles.tripBlock}>
            {!!req.pickupAddress && (
              <Text style={styles.tripLine}>
                <Text style={styles.tripLabel}>Pickup: </Text>
                {req.pickupAddress}
              </Text>
            )}
            {!!req.dropoffAddress && (
              <Text style={styles.tripLine}>
                <Text style={styles.tripLabel}>Drop-off: </Text>
                {req.dropoffAddress}
              </Text>
            )}
          </View>
        )}

        {!!req.description && (
          <Text style={styles.description}>{req.description}</Text>
        )}

        <View style={styles.metaRow}>
          {!!req.preferredDate && (
            <Text style={styles.metaPill}>📅 {req.preferredDate}</Text>
          )}
          {!!req.preferredTime && (
            <Text style={styles.metaPill}>⏰ {req.preferredTime}</Text>
          )}
          {!!req.budgetRange && (
            <Text style={styles.metaPill}>💵 {req.budgetRange}</Text>
          )}
        </View>

        <View style={styles.contactRow}>
          {!!req.userPhone && (
            <TouchableOpacity
              style={styles.contactButton}
              onPress={() => openTel(req.userPhone)}
            >
              <Text style={styles.contactButtonText}>📞 Call</Text>
            </TouchableOpacity>
          )}
          {!!req.userPhone && (
            <TouchableOpacity
              style={styles.contactButton}
              onPress={() => openSms(req.userPhone, req.userName, businessName)}
            >
              <Text style={styles.contactButtonText}>💬 Text</Text>
            </TouchableOpacity>
          )}
          {!!req.userEmail && (
            <TouchableOpacity
              style={styles.contactButton}
              onPress={() => openMail(req.userEmail, req.serviceName)}
            >
              <Text style={styles.contactButtonText}>✉️ Email</Text>
            </TouchableOpacity>
          )}
        </View>

        {myResponses.length > 0 && (
          <View style={styles.responsesBlock}>
            <Text style={styles.responsesTitle}>Your replies</Text>
            {myResponses.map((r, i) => (
              <View key={`${req.id}-resp-${i}`} style={styles.responseRow}>
                <Text style={styles.responseKind}>
                  {responseLabel(r.response)}
                  {typeof r.proposedPrice === 'number' ? ` · $${r.proposedPrice.toFixed(2)}` : ''}
                </Text>
                {!!r.message && <Text style={styles.responseMessage}>"{r.message}"</Text>}
                <Text style={styles.responseTime}>{formatRelative(r.respondedAt)}</Text>
              </View>
            ))}
          </View>
        )}

        {isPending && (
          <View style={styles.actionsRow}>
            <TouchableOpacity
              style={[styles.actionBtn, styles.actionPrimary]}
              onPress={() => openResponseModal(req, 'interested')}
            >
              <Text style={styles.actionPrimaryText}>Send quote</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.actionBtn, styles.actionSecondary]}
              onPress={() => openResponseModal(req, 'need_more_info')}
            >
              <Text style={styles.actionSecondaryText}>Ask for info</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.actionBtn, styles.actionDanger]}
              onPress={() =>
                Alert.alert(
                  'Decline request?',
                  `Mark this request from ${req.userName || 'this customer'} as unavailable?`,
                  [
                    { text: 'Cancel', style: 'cancel' },
                    {
                      text: 'Decline',
                      style: 'destructive',
                      onPress: () => openResponseModal(req, 'not_available'),
                    },
                  ]
                )
              }
            >
              <Text style={styles.actionDangerText}>Decline</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Quote requests</Text>
        <View style={styles.placeholder} />
      </View>

      <View style={styles.filterBar}>
        <FilterChip
          label={`Pending${pendingCount ? ` (${pendingCount})` : ''}`}
          active={filter === 'pending'}
          onPress={() => setFilter('pending')}
        />
        <FilterChip
          label={`All (${requests.length})`}
          active={filter === 'all'}
          onPress={() => setFilter('all')}
        />
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color="#007AFF" />
          <Text style={styles.centerText}>Loading requests…</Text>
        </View>
      ) : !businessId ? (
        <View style={styles.center}>
          <Text style={styles.centerText}>Sign in as a business owner to view requests.</Text>
        </View>
      ) : visibleRequests.length === 0 ? (
        <ScrollView
          contentContainerStyle={styles.centerContent}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        >
          <Text style={styles.emptyTitle}>No {filter === 'pending' ? 'pending ' : ''}requests</Text>
          <Text style={styles.emptySubtitle}>
            When customers send you quote or estimate requests from BookAPT, they'll appear here.
          </Text>
        </ScrollView>
      ) : (
        <ScrollView
          contentContainerStyle={styles.listContent}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        >
          {visibleRequests.map(renderRequestCard)}
        </ScrollView>
      )}

      <Modal
        visible={!!activeRequest}
        animationType="slide"
        transparent
        onRequestClose={closeResponseModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {responseKind === 'interested'
                  ? 'Send quote'
                  : responseKind === 'need_more_info'
                  ? 'Ask for more info'
                  : 'Decline request'}
              </Text>
              <TouchableOpacity onPress={closeResponseModal} disabled={submitting}>
                <Text style={styles.modalClose}>✕</Text>
              </TouchableOpacity>
            </View>

            {activeRequest && (
              <Text style={styles.modalSubtitle} numberOfLines={2}>
                {activeRequest.serviceName} · {activeRequest.userName}
              </Text>
            )}

            {responseKind === 'interested' && (
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Your price (USD) *</Text>
                <AppTextInput
                  style={styles.input}
                  value={priceInput}
                  onChangeText={setPriceInput}
                  placeholder="e.g. 85"
                  keyboardType={Platform.OS === 'ios' ? 'decimal-pad' : 'numeric'}
                />
              </View>
            )}

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>
                Message{responseKind === 'need_more_info' ? ' *' : ' (optional)'}
              </Text>
              <AppTextInput
                style={[styles.input, styles.textArea]}
                value={messageInput}
                onChangeText={setMessageInput}
                placeholder={
                  responseKind === 'interested'
                    ? 'Add a note about availability, what is included, etc.'
                    : responseKind === 'need_more_info'
                    ? 'What do you need from the customer?'
                    : 'Optional reason (not shown if blank).'
                }
                multiline
                numberOfLines={4}
                textAlignVertical="top"
              />
            </View>

            <TouchableOpacity
              style={[styles.submitBtn, submitting && styles.submitBtnDisabled]}
              onPress={handleSubmitResponse}
              disabled={submitting}
            >
              <Text style={styles.submitBtnText}>
                {submitting
                  ? 'Saving…'
                  : responseKind === 'interested'
                  ? 'Send quote'
                  : responseKind === 'need_more_info'
                  ? 'Send question'
                  : 'Confirm decline'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// ---------------------------------------------------------------------------
// Helpers / sub-components
// ---------------------------------------------------------------------------

const FilterChip: React.FC<{ label: string; active: boolean; onPress: () => void }> = ({
  label,
  active,
  onPress,
}) => (
  <TouchableOpacity
    style={[styles.chip, active && styles.chipActive]}
    onPress={onPress}
    activeOpacity={0.8}
  >
    <Text style={[styles.chipText, active && styles.chipTextActive]}>{label}</Text>
  </TouchableOpacity>
);

const StatusBadge: React.FC<{ status: CustomServiceRequest['status'] }> = ({ status }) => {
  const styleMap: Record<CustomServiceRequest['status'], { bg: string; color: string; label: string }> = {
    pending: { bg: '#fff3cd', color: '#856404', label: 'Pending' },
    accepted: { bg: '#d4edda', color: '#155724', label: 'Quoted' },
    declined: { bg: '#f8d7da', color: '#721c24', label: 'Declined' },
    completed: { bg: '#d1ecf1', color: '#0c5460', label: 'Completed' },
  };
  const s = styleMap[status];
  return (
    <View style={[styles.badge, { backgroundColor: s.bg }]}>
      <Text style={[styles.badgeText, { color: s.color }]}>{s.label}</Text>
    </View>
  );
};

const responseLabel = (kind: ResponseKind | string): string => {
  if (kind === 'interested') return 'Sent quote';
  if (kind === 'not_available') return 'Declined';
  if (kind === 'need_more_info') return 'Asked for info';
  return kind;
};

const formatRelative = (iso?: string): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return date.toLocaleDateString();
};

const openTel = (phone: string) => {
  Linking.openURL(`tel:${phone}`).catch(() =>
    Alert.alert('Cannot open dialer', `Please call ${phone} manually.`)
  );
};

const openSms = (phone: string, name?: string, businessName?: string) => {
  const body = encodeURIComponent(
    `Hi${name ? ` ${name}` : ''}, this is ${businessName || 'BookAPT'} about your quote request.`
  );
  const sep = Platform.OS === 'ios' ? '&' : '?';
  Linking.openURL(`sms:${phone}${sep}body=${body}`).catch(() =>
    Alert.alert('Cannot open messages', `Please text ${phone} manually.`)
  );
};

const openMail = (email: string, subject?: string) => {
  const subj = encodeURIComponent(subject ? `Your quote request: ${subject}` : 'Your quote request');
  Linking.openURL(`mailto:${email}?subject=${subj}`).catch(() =>
    Alert.alert('Cannot open email', `Please email ${email} manually.`)
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 50,
    paddingBottom: 14,
    backgroundColor: '#007AFF',
  },
  backButton: { padding: 8, minWidth: 60 },
  backButtonText: { color: 'white', fontSize: 16, fontWeight: '600' },
  headerTitle: { color: 'white', fontSize: 18, fontWeight: 'bold' },
  placeholder: { width: 60 },

  filterBar: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    gap: 8,
  },
  chip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 16,
    backgroundColor: '#eef0f3',
  },
  chipActive: { backgroundColor: '#007AFF' },
  chipText: { color: '#444', fontWeight: '600', fontSize: 13 },
  chipTextActive: { color: 'white' },

  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  centerContent: { flexGrow: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  centerText: { marginTop: 12, color: '#555', textAlign: 'center' },
  emptyTitle: { fontSize: 18, fontWeight: '600', color: '#333', marginBottom: 8 },
  emptySubtitle: { fontSize: 14, color: '#666', textAlign: 'center', lineHeight: 20 },

  listContent: { padding: 16, paddingBottom: 32 },

  card: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOpacity: 0.06,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 4,
    elevation: 2,
  },
  cardHeaderRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8 },
  cardTitle: { fontSize: 16, fontWeight: '700', color: '#222' },
  cardSubtitle: { fontSize: 13, color: '#666', marginTop: 2 },

  badge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, marginLeft: 8 },
  badgeText: { fontSize: 12, fontWeight: '700' },

  tripBlock: {
    backgroundColor: '#f7f9fc',
    padding: 10,
    borderRadius: 8,
    marginVertical: 8,
  },
  tripLine: { fontSize: 13, color: '#333', lineHeight: 18 },
  tripLabel: { fontWeight: '700', color: '#444' },

  description: { fontSize: 14, color: '#333', lineHeight: 20, marginVertical: 8 },

  metaRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 10 },
  metaPill: {
    backgroundColor: '#eef4ff',
    color: '#1d4ed8',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 10,
    fontSize: 12,
    fontWeight: '600',
    marginRight: 6,
    marginBottom: 4,
  },

  contactRow: { flexDirection: 'row', gap: 8, marginBottom: 10 },
  contactButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#f0f1f5',
    marginRight: 8,
  },
  contactButtonText: { fontSize: 13, fontWeight: '600', color: '#333' },

  responsesBlock: {
    borderTopWidth: 1,
    borderTopColor: '#eee',
    marginTop: 4,
    paddingTop: 10,
    marginBottom: 6,
  },
  responsesTitle: { fontSize: 13, fontWeight: '700', color: '#444', marginBottom: 6 },
  responseRow: { marginBottom: 8 },
  responseKind: { fontSize: 13, fontWeight: '600', color: '#333' },
  responseMessage: { fontSize: 13, color: '#555', marginTop: 2, fontStyle: 'italic' },
  responseTime: { fontSize: 11, color: '#888', marginTop: 2 },

  actionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 },
  actionBtn: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    marginRight: 8,
    marginTop: 4,
  },
  actionPrimary: { backgroundColor: '#28a745' },
  actionPrimaryText: { color: 'white', fontWeight: '700', fontSize: 13 },
  actionSecondary: { backgroundColor: '#eef4ff' },
  actionSecondaryText: { color: '#1d4ed8', fontWeight: '700', fontSize: 13 },
  actionDanger: { backgroundColor: '#fde2e1' },
  actionDangerText: { color: '#b02a37', fontWeight: '700', fontSize: 13 },

  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: 'white',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    padding: 20,
    paddingBottom: 32,
  },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { fontSize: 18, fontWeight: '700', color: '#222' },
  modalClose: { fontSize: 22, color: '#666', padding: 4 },
  modalSubtitle: { color: '#555', marginTop: 4, marginBottom: 8 },

  inputGroup: { marginTop: 14 },
  inputLabel: { fontSize: 13, fontWeight: '600', color: '#444', marginBottom: 6 },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 15,
    backgroundColor: '#fff',
  },
  textArea: { minHeight: 90 },

  submitBtn: {
    marginTop: 20,
    backgroundColor: '#007AFF',
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  submitBtnDisabled: { backgroundColor: '#9bbcec' },
  submitBtnText: { color: 'white', fontWeight: '700', fontSize: 15 },
});

export default QuoteRequestsScreen;
